import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Product, SizeVariant } from "@/data/products";
import type { OfferInfo } from "@/utils/offerUtils";

interface ProductsContextType {
  products: Product[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used within ProductsProvider");
  return ctx;
};

function mapOffer(row: any, prefix = ""): OfferInfo | undefined {
  const enabled = row[`${prefix}offer_enabled`];
  if (!enabled) return undefined;
  return {
    enabled: true,
    type: (row[`${prefix}offer_type`] || "percentage") as "percentage" | "fixed",
    value: Number(row[`${prefix}offer_value`] || 0),
    startDate: row[`${prefix}offer_start_date`] ?? undefined,
    endDate: row[`${prefix}offer_end_date`] ?? undefined,
    badge: row[`${prefix}offer_badge`] ?? undefined,
    badgeAr: row[`${prefix}offer_badge_ar`] ?? undefined,
  };
}

// Convert DB row + variants to the Product interface used by the frontend
function mapDbToProduct(row: any, variants: any[], subVariantsByVariant: Map<string, any[]>): Product {
  const sizeVariants: SizeVariant[] | undefined =
    variants.length > 0
      ? variants
          .sort((a: any, b: any) => a.sort_order - b.sort_order)
           .map((v: any) => ({
            id: v.id,
            size: v.size,
            label: v.label,
            labelAr: v.label_ar,
            price: Number(v.price),
            image: v.image_url,
            imageIsCustom: v.image_is_custom ?? false,
            packagingImage: v.packaging_image_url ?? undefined,
            lifestyleImage: v.lifestyle_image_url ?? undefined,
            ingredientsImage: v.ingredients_image_url ?? undefined,
            offer: mapOffer(v),
            stockQuantity: v.stock_quantity ?? 100,
            stockStatus: v.stock_status ?? "in_stock",
            isFeatured: v.is_featured ?? false,
            poeticName: v.poetic_name ?? undefined,
            poeticNameAr: v.poetic_name_ar ?? undefined,
            story: v.story ?? undefined,
            storyAr: v.story_ar ?? undefined,
            colorHex: v.color_hex ?? undefined,
            subVariants: (subVariantsByVariant.get(v.id) || [])
              .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
              .map((sv: any) => ({
                id: sv.id,
                colorHex: sv.color_hex ?? null,
                poeticName: sv.poetic_name ?? null,
                poeticNameAr: sv.poetic_name_ar ?? null,
                scent: sv.scent ?? null,
                scentAr: sv.scent_ar ?? null,
                story: sv.story ?? null,
                storyAr: sv.story_ar ?? null,
                priceDelta: Number(sv.price_delta ?? 0),
                stockQuantity: sv.stock_quantity ?? 100,
                stockStatus: sv.stock_status ?? "in_stock",
                imageUrl: sv.image_url ?? null,
                sortOrder: sv.sort_order ?? 0,
              })),
          }))
      : undefined;

  return {
    id: row.id,
    name: row.name,
    nameAr: row.name_ar,
    price: Number(row.price),
    category: row.category,
    description: row.description,
    descriptionAr: row.description_ar,
    shortDescription: row.short_description ?? undefined,
    shortDescriptionAr: row.short_description_ar ?? undefined,
    size: row.size ?? undefined,
    ingredients: row.ingredients as string[],
    ingredientsAr: row.ingredients_ar as string[],
    benefits: row.benefits as string[],
    benefitsAr: row.benefits_ar as string[],
    howToUse: row.how_to_use ?? undefined,
    howToUseAr: row.how_to_use_ar ?? undefined,
    features: (row.features as string[]) ?? undefined,
    featuresAr: (row.features_ar as string[]) ?? undefined,
    image: row.image_url,
    packagingImage: row.packaging_image_url ?? undefined,
    lifestyleImage: row.lifestyle_image_url ?? undefined,
    ingredientsImage: row.ingredients_image_url ?? undefined,
    badge: row.badge ?? undefined,
    badgeAr: row.badge_ar ?? undefined,
    offer: mapOffer(row),
    stockQuantity: row.stock_quantity ?? 100,
    stockStatus: row.stock_status ?? "in_stock",
    flashDealEligible: row.flash_deal_eligible ?? false,
    isFeatured: row.is_featured ?? false,
    sizeVariants,
    essence: row.essence
      ? {
          name: (row.essence as any).name,
          nameAr: (row.essence as any).nameAr,
          description: (row.essence as any).description,
          descriptionAr: (row.essence as any).descriptionAr,
          benefits: (row.essence as any).benefits,
          benefitsAr: (row.essence as any).benefitsAr,
        }
      : undefined,
    faqs: Array.isArray(row.faqs) ? (row.faqs as any[]).map((f: any) => ({
      question: String(f?.question ?? ""),
      questionAr: String(f?.questionAr ?? f?.question_ar ?? ""),
      answer: String(f?.answer ?? ""),
      answerAr: String(f?.answerAr ?? f?.answer_ar ?? ""),
    })).filter((f) => (f.question && f.answer) || (f.questionAr && f.answerAr)) : [],
  };
}

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async (opts?: { silent?: boolean }) => {
    // Only flip the global loading flag on the first fetch. Background
    // revalidations (60s polling + realtime events) must NOT toggle loading,
    // otherwise the storefront grid unmounts/remounts and visibly "flashes".
    if (!opts?.silent) setLoading(true);
    const [prodRes, varRes, subVarRes] = await Promise.all([
      supabase.from("products").select("*").order("sort_order"),
      supabase.from("product_variants").select("*").order("sort_order"),
      supabase.from("product_sub_variants").select("*").order("sort_order"),
    ]);

    if (prodRes.data) {
      const variantsByProduct = new Map<string, any[]>();
      for (const v of varRes.data || []) {
        const list = variantsByProduct.get(v.product_id) || [];
        list.push(v);
        variantsByProduct.set(v.product_id, list);
      }

      const subVariantsByVariant = new Map<string, any[]>();
      for (const sv of subVarRes.data || []) {
        const list = subVariantsByVariant.get(sv.variant_id) || [];
        list.push(sv);
        subVariantsByVariant.set(sv.variant_id, list);
      }

      const mapped = prodRes.data.map((row) =>
        mapDbToProduct(row, variantsByProduct.get(row.id) || [], subVariantsByVariant)
      );
      setProducts(mapped);
    }
    if (!opts?.silent) setLoading(false);
  }, []);

  useEffect(() => {
    // Initial fetch shows the spinner.
    fetchProducts();

    // Realtime subscriptions: refetch SILENTLY so the storefront doesn't flash.
    const channel = supabase
      .channel("public-products-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        () => fetchProducts({ silent: true })
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "product_variants" },
        () => fetchProducts({ silent: true })
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "product_sub_variants" },
        () => fetchProducts({ silent: true })
      )
      .subscribe();

    // Fallback polling every 60 seconds in case realtime drops (also silent).
    const interval = setInterval(() => fetchProducts({ silent: true }), 60000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [fetchProducts]);

  return (
    <ProductsContext.Provider value={{ products, loading, refresh: fetchProducts }}>
      {children}
    </ProductsContext.Provider>
  );
};
